<template>
  <div class="content-wrapper">
    <h2 class="page-titles">Products</h2>
    <div>
      <Loading text="Loading..." v-if="loadStatus" />
      <template v-if="!loadStatus">
        <div v-if="storeInventoryList.length">
          <vue-good-table
            :columns="columns"
            :rows="storeInventoryList"
            styleClass="vgt-table striped"
            @on-cell-click="onCellClick"
            :row-style-class="rowStyleClassFn"
            style="width: 100%"
            :search-options="{
              enabled: true,
              externalQuery: searchTerm
            }"
            :pagination-options="{
              enabled: true,
              mode: 'records',
              perPage: 25,
              perPageDropdown: [25, 50, 100],
              dropdownAllowAll: false,
              nextLabel: 'next',
              prevLabel: 'prev',
              rowsPerPageLabel: 'Rows per page',
              ofLabel: 'of',
              pageLabel: 'page',
              allLabel: 'All'
            }"
            @on-page-change="onPageChange"
          >
            >
            <template slot="table-row" slot-scope="storeInventoryList">
              <div
                v-if="storeInventoryList.column.field === 'image'"
                class="image-holder"
              >
                <img
                  :src="productImage(storeInventoryList.row)"
                  class="product-icon"
                  @error="handleBrokenImage($event)"
                />

                <div
                  class="table-edit-options"
                  v-if="
                    currentEditRowIndex === storeInventoryList.row.originalIndex
                  "
                  @click="showEditModal"
                >
                  <span>Edit</span>
                </div>
              </div>

              <template v-else-if="storeInventoryList.column.field === 'title'">
                <div
                  v-if="
                    currentEditRowIndex === storeInventoryList.row.originalIndex
                  "
                >
                  <input
                    class="table-input"
                    type="text"
                    v-model="editedRow.title"
                  />
                </div>
                <span v-else>{{ storeInventoryList.row.title }}</span>
              </template>
              <div v-else-if="storeInventoryList.column.field === 'cost'">
                <div
                  v-if="
                    currentEditRowIndex === storeInventoryList.row.originalIndex
                  "
                >
                  <input
                    class="table-input"
                    type="text"
                    v-model="editedRow.cost"
                  />
                </div>
                <span v-else>{{
                  formatCurrency(storeInventoryList.row.cost)
                }}</span>
              </div>
              <div
                v-else-if="storeInventoryList.column.field === 'description'"
              >
                <div
                  v-if="
                    currentEditRowIndex === storeInventoryList.row.originalIndex
                  "
                >
                  <textarea
                    class="table-input"
                    placeholder="Add Description"
                    type="text"
                    v-model="editedRow.description"
                  />
                  <p class="description-input-info">
                    <small>Press Return to add a new line</small>
                  </p>
                </div>
                <template v-else>
                  <span v-if="storeInventoryList.row.description">
                    {{ storeInventoryList.row.description }}
                  </span>
                  <span class="no-description" v-else>No description</span>
                </template>
              </div>
              <template v-else-if="storeInventoryList.column.field === 'edit'">
                <div
                  v-if="
                    currentEditRowIndex === storeInventoryList.row.originalIndex
                  "
                >
                  <div class="inventory-edit-actions">
                    <span class="cancel" @click.stop="cancelEdition"
                      >Cancel</span
                    >
                    <button
                      @click.stop="saveEdition"
                      class="button-gemnote button-gemnote--tiny"
                    >
                      <span>Save</span>
                    </button>
                  </div>
                </div>
                <div v-else class="tpeam-edit">
                  <div class="table-edit-options">
                    <span>Edit</span>
                  </div>
                </div>
              </template>
            </template>
          </vue-good-table>
        </div>
        <div v-else>
          <StoreInventoryZeroState />
        </div>
      </template>
    </div>

    <EditModal :showFooter="true" v-if="showModal" @onConfirm="onConfirm">
      <template slot="header">
        <h4 class="header">{{ editedRow.title }}</h4>
      </template>
      <template slot="body">
        <EditProductForm :product="editedRow" />
      </template>
    </EditModal>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { scrollPageToTop, handleBrokenImage } from '@/helpers'
import { VueGoodTable } from 'vue-good-table'
import Util from '../../mixins/Util'
export default {
  name: 'EcommerceProducts',
  mixins: [Util],
  components: {
    VueGoodTable,
    EditModal: () => import('./EcommerceEditProductModal.vue'),
    EditProductForm: () => import('./EditProductForm.vue'),
    StoreInventoryZeroState: () =>
      import('@/components/Inventory/StoreInventoryZeroState')
  },
  async created() {
    await this.$store.dispatch('ecommerce/getStoreDetails')
    await this.$store.dispatch(
      'ecommerce/getStoreProducts',
      this.shopDetails.slug
    )
  },
  computed: {
    loadStatus() {
      return this.loadingStoreInventory
    },
    ...mapState({
      showModal: state => state.modal.showModal,
      loadingStoreInventory: state => state.ecommerce.loadingProducts,
      storeInventoryList: state => state.ecommerce.storeProducts,
      searchTerm: state => state.globalTracking.searchTerm,
      shopDetails: state => state.ecommerce.shopDetails
    })
  },
  data() {
    return {
      handleBrokenImage: handleBrokenImage,
      currentEditRowIndex: null,
      currentUpdatedRowIndex: null,
      isEditingRow: false,
      editedRow: {},
      columns: [
        {
          label: 'Product',
          field: 'image',
          width: '150px',
          sortable: false
        },
        {
          label: 'Product Name',
          field: 'title'
        },
        {
          label: 'Price',
          field: 'cost',
          width: '100px',
          hidden: !this.showPrices()
        },
        {
          label: 'Product Description',
          field: 'description'
        },
        {
          label: 'Edit',
          field: 'edit'
        }
      ]
    }
  },
  methods: {
    productImage(product) {
      return product.defaultImageUrl ? product.defaultImageUrl : product.image
    },
    onConfirm() {
      this.$store.dispatch('ecommerce/updateProductImages', {
        id: parseInt(this.editedRow.id)
      })

      this.currentUpdatedRowIndex = this.currentEditRowIndex
      this.currentEditRowIndex = null
      this.isEditingRow = false
      this.currentUpdatedRowIndex = null
    },
    toggleVisibility(event, row) {
      this.$store.dispatch('inventory/toggleAdminOnlyProduct', {
        id: parseInt(row.id),
        adminOnly: row.adminOnly,
        category: row.category === 'Gift' ? 'Product' : 'Bundle',
        originalIndex: row.originalIndex
      })
    },
    showPrices() {
      return this.$store.getters['team/showPrices']
    },
    onPageChange() {
      scrollPageToTop()
    },
    rowStyleClassFn(row) {
      return row.originalIndex === this.currentUpdatedRowIndex
        ? 'updating-row'
        : 'not-loading'
    },

    onCellClick({ column, row }) {
      switch (column.field) {
        case 'edit':
          if (!this.isEditingRow) {
            this.currentEditRowIndex = row.originalIndex
            this.editedRow = Object.assign({}, row)
            this.isEditingRow = true
          }
          break
      }
    },
    cancelEdition() {
      this.currentEditRowIndex = -1
      this.editedRow = null
      this.isEditingRow = false
    },

    showEditModal() {
      this.$store.dispatch('modal/showModal')
      // const product = { ...this.editedRow }
    },

    async saveEdition() {
      const product = { ...this.editedRow }
      this.currentUpdatedRowIndex = this.currentEditRowIndex
      this.currentEditRowIndex = null
      this.isEditingRow = false
      await this.$store.dispatch('inventory/updateProduct', product)
      this.currentUpdatedRowIndex = null
    }
  }
}
</script>

<style scoped lang="scss">
@import '../../global.scss';
/* https://github.com/euvl/vue-js-toggle-button */

.header {
  font-family: $editorial;
  font-size: 2rem;
  font-weight: normal;
}

small {
  font-weight: bold;
  font-size: 0.625rem;
}

h5 {
  margin: 3rem;
}
.image-holder {
  display: flex;
  width: 50%;
  justify-content: center;
  flex-direction: column;

  .product-icon {
    height: 45px;
    object-fit: contain;
    padding: 0px 15px;
  }
  .table-edit-options {
    justify-content: center;
  }
}

.inventory-edit-actions {
  display: flex;
  gap: 20px;
  align-items: center;

  .cancel {
    cursor: pointer;
  }
}

.table-input {
  border: 0.5px solid #a09c95;
  background: #dfdad2;
  padding: 10px;
  resize: none;

  &:focus {
    outline: none;
  }
}

textarea.table-input {
  width: 100%;
  font-family: $inter;
  font-size: 0.875rem;
}

.description-input-info {
  font-size: 0.625rem;
  text-align: right;
  color: rgba(35, 34, 32, 0.5);
}

.no-description {
  color: rgba(36, 41, 44, 0.5);
}

.button-gemnote--tiny {
  height: 1.5rem;
  font-weight: 500;
  font-size: 0.625rem;
  width: auto;
  padding: 0 1.125rem !important;
}
</style>
